/**
 * 主线程入口：输入 → 指令 → Worker，快照 → 渲染。
 *
 * 主线程绝不直接修改模拟状态。所有玩家操作都被编码成 Command 发给 Worker，
 * 这是 Lockstep 联机的前提——同一套指令流在两台机器上必须产生同样的结果。
 * 当前是单机，但指令通道已经按联机的形状搭好了。
 */

import { Renderer } from './render/renderer.ts';
import { Hud } from './ui/hud.ts';
import { MAP_W, MAP_H, PLAYER_HUMAN, Kind, SNAP_STRIDE, TICKS_PER_SEC, SnapFlag } from './shared/constants.ts';
import type { Command, MainToWorker, SnapMeta, WorkerToMain } from './shared/protocol.ts';
import { DEF_BY_KEY, defOf, Flag, type UnitDef } from './sim/data/types.ts';
import { fromFloat } from './sim/fixed/fix.ts';
import { Tile } from './sim/map/terrain.ts';
import SimWorker from './sim/worker.ts?worker';

const canvas = document.getElementById('stage') as HTMLCanvasElement;
const renderer = new Renderer();

let worker: Worker;
let meta: SnapMeta | null = null;
let selection: number[] = [];
let buildMode: UnitDef | null = null;
let attackMoveMode = false;
let terrainTiles = new Uint8Array(MAP_W * MAP_H);
let blockedApprox = new Uint8Array(MAP_W * MAP_H);
let pendingCmds: Command[] = [];
let bases: Array<{ x: number; y: number }> = [];
let lastSnapTime = 0;

const hud = new Hud({
  onBuildSelect: (def) => enterBuildMode(def),
  onProduce: (def) => {
    send({ t: 'produce', player: PLAYER_HUMAN, typeId: def.id });
    hud.toast(`${def.name} 已编入生产队列`);
  },
  onCancel: (def) => send({ t: 'cancel', player: PLAYER_HUMAN, typeId: def.id }),
  onMinimapClick: (tx, ty, right) => {
    if (right) {
      issueMoveToTile(tx, ty, false);
    } else {
      renderer.centerOnTile(tx, ty);
    }
  },
  onRestart: () => startGame((Math.random() * 0x7fffffff) | 0),
});

// ─────────────────────────────────────────────────────────
// Worker 通信
// ─────────────────────────────────────────────────────────

function send(cmd: Command): void {
  pendingCmds.push(cmd);
}

function flushCommands(): void {
  if (pendingCmds.length === 0) return;
  const msg: MainToWorker = { t: 'cmd', cmds: pendingCmds };
  worker.postMessage(msg);
  pendingCmds = [];
}

function startGame(seed: number): void {
  renderer.resetWorld();
  if (worker) worker.terminate();
  worker = new SimWorker();
  selection = [];
  buildMode = null;
  meta = null;
  worker.onmessage = (e: MessageEvent<WorkerToMain>) => {
    const m = e.data;
    if (m.t === 'ready') {
      terrainTiles = new Uint8Array(m.tiles);
      blockedApprox.fill(0);
      bases = m.bases;
      renderer.buildTerrain(terrainTiles, m.rifts, m.bases);
      renderer.updateOre(new Int32Array(m.ore));
      renderer.centerOnTile(bases[PLAYER_HUMAN]!.x, bases[PLAYER_HUMAN]!.y);
      hud.hideLoading();
      return;
    }
    // snap
    const ents = new Int32Array(m.ents);
    meta = m.meta;
    renderer.updateFog(new Uint8Array(m.fog));
    renderer.applySnapshot(ents, m.meta.entCount);
    if (m.oreDirty) renderer.updateOre(new Int32Array(m.oreDirty));
    refreshBlockedApprox(ents, m.meta.entCount);
    lastSnapTime = performance.now();
    // 归还缓冲，让 Worker 复用（零分配双缓冲）
    const ret: MainToWorker = { t: 'return', ents: m.ents, fog: m.fog };
    worker.postMessage(ret, [m.ents, m.fog]);
    // 选中集合里已阵亡的单位要剔除
    if (selection.length > 0) {
      const before = selection.length;
      selection = selection.filter((id) => renderer.viewOf(id) !== null);
      if (selection.length !== before) syncSelection();
    }
  };
  const init: MainToWorker = { t: 'init', seed };
  worker.postMessage(init);
}

/**
 * 从快照里重建一份「建筑占地」近似图，仅用于建造预览的合法性提示。
 * 权威判定仍在模拟层——预览允许有一帧的误差，真正非法的放置会被 Worker 静默拒绝。
 */
function refreshBlockedApprox(ents: Int32Array, count: number): void {
  blockedApprox.fill(0);
  for (let i = 0; i < count; i++) {
    const o = i * SNAP_STRIDE;
    if (ents[o + 3] !== Kind.Building) continue;
    const def = defOf(ents[o + 5]!);
    const fp = def.footprint;
    const cx = ents[o + 1]! / 65536;
    const cy = ents[o + 2]! / 65536;
    const tx = Math.round(cx - fp / 2);
    const ty = Math.round(cy - fp / 2);
    for (let dy = 0; dy < fp; dy++) {
      for (let dx = 0; dx < fp; dx++) {
        const x = tx + dx;
        const y = ty + dy;
        if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
        blockedApprox[y * MAP_W + x] = 1;
      }
    }
  }
}

// ─────────────────────────────────────────────────────────
// 建造模式
// ─────────────────────────────────────────────────────────

function enterBuildMode(def: UnitDef): void {
  buildMode = def;
  attackMoveMode = false;
  hud.setBuildHint(`放置「${def.name}」：左键确认，右键或 ESC 取消`);
}

function exitBuildMode(): void {
  buildMode = null;
  hud.setBuildHint('选定建造厂后可展开工事编制');
}

/** 建造预览的合法性：与模拟层 canPlace 保持同样的规则 */
function canPlaceApprox(def: UnitDef, tx: number, ty: number): boolean {
  const fp = def.footprint;
  for (let dy = 0; dy < fp; dy++) {
    for (let dx = 0; dx < fp; dx++) {
      const x = tx + dx;
      const y = ty + dy;
      if (x < 1 || y < 1 || x >= MAP_W - 1 || y >= MAP_H - 1) return false;
      const i = y * MAP_W + x;
      if (terrainTiles[i] === Tile.Rock || terrainTiles[i] === Tile.Ore) return false;
      if (blockedApprox[i]) return false;
    }
  }
  // 建造半径：必须靠近己方已有建筑
  const cx = tx + fp / 2;
  const cy = ty + fp / 2;
  for (const b of renderer.entityBlips()) {
    if (b.owner !== PLAYER_HUMAN || b.kind !== Kind.Building) continue;
    if (Math.hypot(b.tx - cx, b.ty - cy) <= 12) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────
// 选择与下令
// ─────────────────────────────────────────────────────────

function syncSelection(): void {
  const msg: MainToWorker = { t: 'select', units: selection };
  worker.postMessage(msg);
  hud.updateSelection(selection, renderer);
}

function selectSingle(id: number, additive: boolean): void {
  const v = renderer.viewOf(id);
  if (!v || v.owner !== PLAYER_HUMAN) {
    if (!additive) {
      selection = [];
      syncSelection();
    }
    return;
  }
  if (additive) {
    if (!selection.includes(id)) selection.push(id);
  } else {
    selection = [id];
  }
  syncSelection();
}

/** 双击：选中同类型的全部己方单位 */
function selectSameType(id: number): void {
  const v = renderer.viewOf(id);
  if (!v) return;
  const out: number[] = [];
  for (const u of renderer.unitsOf(PLAYER_HUMAN)) {
    if (u.typeId === v.typeId) out.push(u.id);
  }
  if (out.length > 0) {
    selection = out;
    syncSelection();
  }
}

function issueMoveToTile(tx: number, ty: number, attack: boolean): void {
  if (selection.length === 0) return;
  send({
    t: 'move',
    units: selection.slice(),
    x: fromFloat(tx + 0.5),
    y: fromFloat(ty + 0.5),
    attackMove: attack,
  });
}

function issueOrderAt(sx: number, sy: number): void {
  if (selection.length === 0) return;
  const hit = renderer.pickAt(sx, sy);
  if (hit > 0) {
    const v = renderer.viewOf(hit);
    if (v && v.owner !== PLAYER_HUMAN) {
      send({ t: 'attack', units: selection.slice(), target: hit });
      hud.toast(`攻击 ${defOf(v.typeId).name}`);
      return;
    }
    if (v && v.owner === PLAYER_HUMAN && v.hp < v.maxHp && (v.flags & SnapFlag.Constructing) === 0) {
      const hasRepair = selection.some((id) => {
        const sv = renderer.viewOf(id);
        return sv !== null && (defOf(sv.typeId).flags & Flag.Repairer) !== 0;
      });
      if (hasRepair) {
        send({ t: 'attack', units: selection.slice(), target: hit });
        hud.toast(`维修 ${defOf(v.typeId).name}`);
        return;
      }
    }
  }
  const t = renderer.screenToTileF(sx, sy);
  send({
    t: 'move',
    units: selection.slice(),
    x: fromFloat(t.tx),
    y: fromFloat(t.ty),
    attackMove: attackMoveMode,
  });
  attackMoveMode = false;
  canvas.style.cursor = '';
}

// ─────────────────────────────────────────────────────────
// 输入
// ─────────────────────────────────────────────────────────

let dragging = false;
let dragStartX = 0;
let dragStartY = 0;
let dragCurX = 0;
let dragCurY = 0;
let panning = false;
let mouseX = 0;
let mouseY = 0;
let lastClickTime = 0;
let lastClickId = -1;
let pointerInside = false;
const keys = new Set<string>();

/**
 * 画布局部坐标。
 * 不用 e.offsetX：它在存在 CSS 变换时会失真，且合成事件不一定携带该字段。
 * 以包围盒换算既稳妥，又让自动化测试可以直接派发事件驱动输入层。
 */
function localPos(e: MouseEvent): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

function setupInput(): void {
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('mouseenter', () => (pointerInside = true));
  canvas.addEventListener('mouseleave', () => {
    pointerInside = false;
    renderer.camera.setPan(0, 0);
  });

  canvas.addEventListener('mousedown', (e) => {
    const p = localPos(e);
    mouseX = p.x;
    mouseY = p.y;
    pointerInside = true;
    if (e.button === 1) {
      panning = true;
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      if (buildMode) {
        exitBuildMode();
        return;
      }
      if (attackMoveMode) {
        attackMoveMode = false;
        canvas.style.cursor = '';
        return;
      }
      issueOrderAt(p.x, p.y);
      return;
    }
    // 左键
    if (buildMode) {
      const t = renderer.screenToTile(p.x, p.y);
      const fp = buildMode.footprint;
      const tx = t.tx - ((fp - 1) >> 1);
      const ty = t.ty - ((fp - 1) >> 1);
      if (!canPlaceApprox(buildMode, tx, ty)) {
        hud.toast('无法在此建造：需靠近己方建筑，且地面须平整无占用');
        return;
      }
      if (meta && meta.seu < buildMode.costSeu) {
        hud.toast('SEU 不足');
        return;
      }
      send({ t: 'build', player: PLAYER_HUMAN, typeId: buildMode.id, tx, ty });
      hud.toast(`开始建造 ${buildMode.name}`);
      if (!e.shiftKey) exitBuildMode();
      return;
    }
    if (attackMoveMode) {
      issueOrderAt(p.x, p.y);
      return;
    }
    dragging = true;
    dragStartX = dragCurX = p.x;
    dragStartY = dragCurY = p.y;
  });

  canvas.addEventListener('mousemove', (e) => {
    const p = localPos(e);
    mouseX = p.x;
    mouseY = p.y;
    pointerInside = true;
    if (panning) {
      renderer.camera.panBy(e.movementX, e.movementY);
      return;
    }
    if (dragging) {
      dragCurX = p.x;
      dragCurY = p.y;
      renderer.drawSelectionBox(dragStartX, dragStartY, dragCurX, dragCurY);
    }
  });

  window.addEventListener('mouseup', (e) => {
    if (e.button === 1) {
      panning = false;
      return;
    }
    if (!dragging) return;
    dragging = false;
    renderer.clearSelectionBox();
    const dx = Math.abs(dragCurX - dragStartX);
    const dy = Math.abs(dragCurY - dragStartY);
    if (dx < 5 && dy < 5) {
      const hit = renderer.pickAt(dragStartX, dragStartY);
      const now = performance.now();
      if (hit > 0 && hit === lastClickId && now - lastClickTime < 350) {
        selectSameType(hit);
      } else if (hit > 0) {
        selectSingle(hit, e.shiftKey);
      } else if (!e.shiftKey) {
        selection = [];
        syncSelection();
      }
      lastClickId = hit;
      lastClickTime = now;
    } else {
      const found = renderer.pickInBox(dragStartX, dragStartY, dragCurX, dragCurY);
      selection = e.shiftKey ? [...new Set([...selection, ...found])] : found;
      syncSelection();
      if (found.length > 0) hud.toast(`选定 ${found.length} 个编制单位`);
    }
  });

  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const p = localPos(e);
      renderer.camera.zoomAt(e.deltaY, p.x, p.y);
    },
    { passive: false },
  );

  window.addEventListener('keydown', (e) => {
    keys.add(e.key.toLowerCase());
    switch (e.key.toLowerCase()) {
      case 'escape':
        if (buildMode) exitBuildMode();
        attackMoveMode = false;
        canvas.style.cursor = '';
        break;
      case 'a':
        if (selection.length > 0) {
          attackMoveMode = true;
          canvas.style.cursor = 'crosshair';
          hud.toast('强制攻击移动：点击目标位置');
        }
        break;
      case 's':
        if (selection.length > 0) send({ t: 'stop', units: selection.slice() });
        break;
      case 'h':
        if (bases[PLAYER_HUMAN]) renderer.centerOnTile(bases[PLAYER_HUMAN]!.x, bases[PLAYER_HUMAN]!.y);
        break;
      case 'e': {
        const army: number[] = [];
        for (const u of renderer.unitsOf(PLAYER_HUMAN)) {
          if (defOf(u.typeId).dps > 0) army.push(u.id);
        }
        selection = army;
        syncSelection();
        hud.toast(`已选定全部作战单位（${army.length}）`);
        break;
      }
      case 'q':
        // 集结点
        if (selection.length > 0) {
          const t = renderer.screenToTileF(mouseX, mouseY);
          send({ t: 'rally', player: PLAYER_HUMAN, x: fromFloat(t.tx), y: fromFloat(t.ty) });
          hud.toast('已设置集结点');
        }
        break;
    }
  });

  window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));
  window.addEventListener('blur', () => keys.clear());
}

/**
 * 相机平移：方向键 + 鼠标边缘滚动 + 中键拖拽。
 * 不用 WASD——A/S 已经是强攻与停止，字母键留给指令更符合 RTS 习惯。
 *
 * 边缘滚动必须等指针真正进入过画布才启用：否则页面刚加载时
 * 指针坐标默认为 (0,0)，会被当成「停在左上角」而把相机一路推到地图角落。
 */
function updatePan(): void {
  const SPEED = 900;
  let vx = 0;
  let vy = 0;
  if (keys.has('arrowleft')) vx -= SPEED;
  if (keys.has('arrowright')) vx += SPEED;
  if (keys.has('arrowup')) vy -= SPEED;
  if (keys.has('arrowdown')) vy += SPEED;

  const EDGE = 14;
  if (!panning && pointerInside) {
    if (mouseX < EDGE) vx -= SPEED;
    else if (mouseX > renderer.camera.viewW - EDGE) vx += SPEED;
    if (mouseY < EDGE) vy -= SPEED;
    else if (mouseY > renderer.camera.viewH - EDGE) vy += SPEED;
  }
  renderer.camera.setPan(vx, vy);
}

// ─────────────────────────────────────────────────────────
// 主循环
// ─────────────────────────────────────────────────────────

let lastFrame = performance.now();

function frame(): void {
  const now = performance.now();
  const dt = Math.min(0.1, (now - lastFrame) / 1000);
  lastFrame = now;

  updatePan();
  flushCommands();

  // 快照插值：把 20 Hz 的模拟平滑到显示器刷新率
  renderer.alpha = Math.min(1, (now - lastSnapTime) / (1000 / TICKS_PER_SEC));

  let ghost: { typeId: number; tx: number; ty: number; ok: boolean } | null = null;
  if (buildMode) {
    const t = renderer.screenToTile(mouseX, mouseY);
    const fp = buildMode.footprint;
    const tx = t.tx - ((fp - 1) >> 1);
    const ty = t.ty - ((fp - 1) >> 1);
    ghost = { typeId: buildMode.id, tx, ty, ok: canPlaceApprox(buildMode, tx, ty) };
  }

  renderer.draw(dt, meta ? meta.lawId : 0, ghost);

  if (meta) hud.update(meta, renderer, renderer.fps);

  requestAnimationFrame(frame);
}

// ─────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hud.setBootLog('正在烘焙三维标本与废土地形网格…');
  await renderer.init(canvas);
  hud.setBootLog('正在接入确定性模拟内核…');
  setupInput();
  startGame(20260827);
  requestAnimationFrame(frame);
  // 调试句柄：供开发期在控制台检查渲染与模拟状态
  (window as unknown as { game: unknown }).game = {
    renderer,
    get meta() {
      return meta;
    },
    get selection() {
      return selection;
    },
    send,
    /** 开发期加速：模拟仍是逐 tick 推进的，因此加速不影响确定性 */
    setSpeed(mult: number) {
      const msg: MainToWorker = { t: 'setSpeed', mult };
      worker.postMessage(msg);
    },
    setPaused(paused: boolean) {
      const msg: MainToWorker = { t: 'setPaused', paused };
      worker.postMessage(msg);
    },
    /** 查询某个屏幕点能否放下指定建筑，用于排查「无法在此建造」的具体原因 */
    probe(sx: number, sy: number, key: string) {
      const def = DEF_BY_KEY.get(key);
      if (!def) return { error: 'unknown key' };
      const t = renderer.screenToTile(sx, sy);
      const fp = def.footprint;
      const tx = t.tx - ((fp - 1) >> 1);
      const ty = t.ty - ((fp - 1) >> 1);
      return { tx, ty, ok: canPlaceApprox(def, tx, ty) };
    },
  };
}

void boot();
